import { ArrowIcon, Button, CloseIcon } from 'fronton-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Breakpoint } from 'types/utils';
import { jcm } from 'utils';
import { ImagePlaceholder } from '@navi-toolbox/ui-components';
import { PictureSources } from '../photos-modal.types';
import { Styles } from './modal-layout-desktops.styles';
import { ModalThumbs } from './modal-thumbs';
import { pictureThatIsSameAs } from './utils';

export const ModalLayoutDesktops: React.FC<{
  breakpoint: Breakpoint;
  photosThumbs: PictureSources[];
  showedPicture: PictureSources | null;
  onClose?: () => void;
  setShowedPicture?: (pic: PictureSources | null) => void;
}> = ({ breakpoint, photosThumbs, showedPicture, onClose, setShowedPicture }) => {
  const { t } = useTranslation();
  const { showToPrevBtn, showToNextBtn, showThumbs, showedPicIdx } = React.useMemo(() => {
    const idx = photosThumbs.findIndex(pictureThatIsSameAs(showedPicture));
    return {
      showToPrevBtn: idx > 0,
      showToNextBtn: idx >= 0 && idx < photosThumbs.length - 1,
      showThumbs: idx >= 0,
      showedPicIdx: idx,
    };
  }, [showedPicture, photosThumbs]);

  const showNextPictureCb = React.useCallback(() => {
    if (showedPicIdx < photosThumbs.length - 1) {
      setShowedPicture?.(photosThumbs[showedPicIdx + 1]);
    }
  }, [photosThumbs, showedPicIdx, setShowedPicture]);

  const showPrevPictureCb = React.useCallback(() => {
    if (showedPicIdx > 0) {
      setShowedPicture?.(photosThumbs[showedPicIdx - 1]);
    }
  }, [photosThumbs, showedPicIdx, setShowedPicture]);

  return (
    <div className={Styles.layout}>
      <div
        className={jcm({
          [Styles.thumbsContainer]: true,
          [Styles.thumbsContainerMedium]: breakpoint === 'medium',
        })}
      >
        {showThumbs && (
          <ModalThumbs
            breakpoint={breakpoint}
            showedPicIdx={showedPicIdx}
            photosThumbs={photosThumbs}
            setShowedPicture={setShowedPicture}
          />
        )}

        <div className={Styles.toPrevPhotoContainer}>
          {showToPrevBtn && (
            <Button
              data-qa="modal-prev"
              aria-label={t('ui.buttons.prev')}
              kind="regular"
              variant="secondary"
              size="l"
              rounded
              iconOnly
              onClick={showPrevPictureCb}
            >
              <ArrowIcon direction="left" />
            </Button>
          )}
        </div>
      </div>

      <div
        className={jcm({
          [Styles.photoViewport]: true,
          [Styles.photoViewportMedium]: breakpoint === 'medium',
        })}
        role="region"
      >
        <ImagePlaceholder
          className={Styles.contentPicture}
          src={showedPicture?.urlOriginal || ''}
          loading="lazy"
          alt=""
          fallbackAlt={t('ui.image.alt')}
          fallbackWidth={800}
          fallbackHeight={800}
        />
      </div>

      <div
        className={jcm({
          [Styles.rightContainer]: true,
          [Styles.rightContainerMedium]: breakpoint === 'medium',
        })}
      >
        <div className={Styles.toNextPhotoContainer}>
          {showToNextBtn && (
            <Button
              data-qa="modal-next"
              aria-label={t('ui.buttons.next')}
              kind="regular"
              variant="secondary"
              size="l"
              rounded
              iconOnly
              onClick={showNextPictureCb}
            >
              <ArrowIcon direction="right" />
            </Button>
          )}
        </div>
      </div>

      <Button
        data-qa="modal-close"
        className={Styles.closeButton}
        kind="regular"
        variant="secondary"
        size="l"
        aria-label={t('ui.buttons.close')}
        onClick={onClose}
        rounded
        iconOnly
      >
        <CloseIcon />
      </Button>
    </div>
  );
};
